import React, { useState } from "react";

import { useTranslation } from "react-i18next";

import { WidgetLocation } from "@data-types/live-widget/WidgetLocation";
import { LiveStreamStatus } from "@gp/models";
import { useLiveWidgetStore } from "@state/hooks/live-widgets";
import LmtLsEventStore from "@state/stores/side-widgets/LmtLsEventStore";
import { analyticsTracker } from "@utils/analytics";
import classNames from "classnames";
import { observer } from "mobx-react";

export default observer(function SideWidgetLiveStreamToggleButton(props: {
	lmtLsStore: LmtLsEventStore;
}) {
	const [isHover, setIsHover] = useState(false);

	const liveWidgetStore = useLiveWidgetStore();
	const activeWidget = liveWidgetStore.activeSideWidget;

	const { t } = useTranslation();
	const event = props.lmtLsStore.eventsMap.get(activeWidget?.eventId || "");

	const teamOne = props.lmtLsStore.lookupsStore.teams.get(
		event?.teamOneId || ""
	);
	const teamTwo = props.lmtLsStore.lookupsStore.teams.get(
		event?.teamTwoId || ""
	);
	const homeTeam = teamOne?.name || "";
	const awayTeam = teamTwo?.name || "";

	const sport = props.lmtLsStore.lookupsStore.sports.get(
		event?.sportId || ""
	);

	const tournament = props.lmtLsStore.lookupsStore.tournaments.get(
		event?.tournamentId || ""
	);

	const location: WidgetLocation = WidgetLocation.SIDE;

	const isUpcoming = event?.liveStreamStatus === LiveStreamStatus.Upcoming;
	const isFinished = event?.liveStreamStatus === LiveStreamStatus.Finished;
	const eventHasLsError = liveWidgetStore.eventsWithLsError.has(
		event?.id || ""
	);

	const isDisabled =
		event?.liveStreamStatus == null ||
		isUpcoming ||
		isFinished ||
		eventHasLsError;

	let dataTip = t("LIVE_WIDGETS.LIVE_STREAM.STREAM_UNAVAILABLE");

	if (!eventHasLsError) {
		if (event?.liveStreamStatus === LiveStreamStatus.Upcoming) {
			dataTip = t("LIVE_WIDGETS.LIVE_STREAM.TOOLTIP.UPCOMING");
		} else if (event?.liveStreamStatus === LiveStreamStatus.Live) {
			dataTip = t("LIVE_WIDGETS.LIVE_STREAM.TOOLTIP.LIVE");
		} else if (event?.liveStreamStatus === LiveStreamStatus.Finished) {
			dataTip = t("LIVE_WIDGETS.LIVE_STREAM.TOOLTIP.CANCELLED");
		}
	}

	const isActive = liveWidgetStore.findActiveLiveStreamWidget(
		event?.id || "",
		location
	);

	const buttonClasses = classNames(
		"at-actionLiveStream widget__actions__btn widget__actions__video btn--square btn--square--sml btn--ghost",
		"btn",
		{
			"is-disabled": isDisabled,
		}
	);

	const iconClasses = classNames("u-icon u-icon--med", {
		"u-icon--action--stream--active": !isHover && !isActive && !isDisabled,
		"u-icon--action--stream--selected":
			(isHover || isActive) && !isDisabled,
		"u-icon--action--stream--disabled": isDisabled,
		"u-icon--action--stream--cancelled": isFinished,
		"u-icon--action--stream--upcoming": isUpcoming,
	});

	const handleOnMouseEnter: React.MouseEventHandler<
		HTMLButtonElement
	> = (): void => {
		setIsHover(true);
	};

	const handleOnMouseLeave: React.MouseEventHandler<
		HTMLButtonElement
	> = (): void => {
		setIsHover(false);
	};

	return (
		<button
			className={buttonClasses}
			type="button"
			data-tooltip-id="tv-description"
			data-tooltip-content={dataTip}
			data-tooltip-position-strategy="fixed"
			disabled={isDisabled}
			onMouseEnter={handleOnMouseEnter}
			onMouseLeave={handleOnMouseLeave}
			onClick={() => {
				if (!event) {
					return;
				}
				if (!isActive) {
					analyticsTracker.trackLiveStreamingViewed(
						event?.id || "",
						teamOne?.abrv || "",
						teamTwo?.abrv || "",
						tournament?.name || "",
						sport?.abrv || ""
					);
				}

				const config = {
					eventId: event.id,
					teamOneName: homeTeam || "",
					teamTwoName: awayTeam || "",
					location,
					isUserInitiated: true,
					autoPlay: true,
				};

				liveWidgetStore.addLiveStreamWidget(config);
			}}
		>
			<i className={iconClasses} />
		</button>
	);
});
